/**
 * TimelineEvent
 * Individual timeline event card
 */

import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDbTimestamp } from '@/utils/datetime';
import { ChevronRight, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/utils/cn';
import type { TimelineEvent as TimelineEventType } from '@/types/timeline';
import {
  TIMELINE_EVENT_TYPE_LABELS,
  TIMELINE_EVENT_TYPE_ICONS,
  TIMELINE_ENTITY_TYPE_LABELS,
  getEntityRoute
} from '@/types/timeline';

export interface TimelineEventProps {
  event: TimelineEventType;
  showEntityLink?: boolean;
  onClick?: (event: TimelineEventType) => void;
}

const SENSITIVE_METADATA_KEYS = /(^|_)(id|number|process|case|client|cpf|cnpj|email|phone|whatsapp|name|nome|text|description|subject|location|address|oab)($|_)/i;

function getDisplayMetadata(metadata?: Record<string, unknown>): [string, unknown][] {
  if (!metadata) return [];
  return Object.entries(metadata).filter(([key]) => !SENSITIVE_METADATA_KEYS.test(key));
}

const ALERT_EVENT_TYPES = new Set<TimelineEventType['eventType']>(['task_overdue', 'deadline_missed']);

export function TimelineEvent({
  event,
  showEntityLink = true,
  onClick
}: TimelineEventProps) {

  const handleClick = () => {
    if (onClick) {
      onClick(event);
    }
  };

  // Atraso e prazo perdido ganham o destrutivo do tema; o resto fica neutro
  const isAlert = ALERT_EVENT_TYPES.has(event.eventType);
  const icon = TIMELINE_EVENT_TYPE_ICONS[event.eventType] || '📋';
  const typeLabel = TIMELINE_EVENT_TYPE_LABELS[event.eventType] || event.eventType;

  const createdAt = parseDbTimestamp(event.createdAt);
  const timeAgo = createdAt
    ? formatDistanceToNow(createdAt, { addSuffix: true, locale: ptBR })
    : '-';

  // Entity link
  const entityLink = getEntityRoute(event.entityType, event.entityId);
  const entityLabel = TIMELINE_ENTITY_TYPE_LABELS[event.entityType] || event.entityType;
  const displayMetadata = getDisplayMetadata(event.metadata);

  return (
    <div
      onClick={handleClick}
      className={cn(
        'flex gap-3 rounded-lg border bg-card p-4 transition-shadow hover:shadow-xs sm:gap-4',
        onClick && 'cursor-pointer'
      )}
    >
      <div
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-full border text-xl',
          isAlert ? 'border-destructive/30 bg-destructive/10' : 'bg-muted'
        )}
        aria-hidden="true"
      >
        {icon}
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-start justify-between gap-2">
          <h4 className="line-clamp-1 text-sm font-semibold">{event.title}</h4>
          <time className="shrink-0 text-xs text-muted-foreground">{timeAgo}</time>
        </div>

        {event.description && (
          <p className="mb-2 line-clamp-2 text-sm text-muted-foreground">{event.description}</p>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <Badge variant={isAlert ? 'destructive' : 'secondary'}>{typeLabel}</Badge>

          {event.createdByName && (
            <span className="inline-flex items-center gap-1">
              <User className="size-3" aria-hidden="true" />
              {event.createdByName}
            </span>
          )}

          {showEntityLink && (
            <Link
              to={entityLink}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
            >
              Ver {entityLabel}
              <ChevronRight className="size-3" aria-hidden="true" />
            </Link>
          )}
        </div>

        {displayMetadata.length > 0 && (
          <div className="mt-2 border-t pt-2">
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                Detalhes adicionais
              </summary>
              <div className="mt-2 rounded-sm bg-muted p-2">
                <dl className="space-y-1">
                  {displayMetadata.map(([key, value]) => (
                    <div key={key} className="flex min-w-0 gap-2">
                      <dt className="font-medium">{key}:</dt>
                      <dd className="min-w-0 break-words">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
